import { Button, Col, Spinner2, Text } from 'components';
import { openMobileAppLink } from 'core';
import { Api, theme } from 'lib';
import { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

/**
 * Where a campaign email's primary button lands.
 *
 * `/fancam/:id` is what `CampaignDestinationEnum.FANCAM` has always resolved to, and until this
 * screen existed nothing served it — so every recipient of the only campaign an operator could send
 * arrived at a 404. A fan with the app installed never sees this page: the path is a declared
 * universal link on both platforms, so the app intercepts it first. This is the page for everyone
 * else — no app, or reading on a desktop.
 *
 * ## Why this does not reuse CallToActionModal
 *
 * The plan for the feature that built this page said to. That was wrong on inspection: the modal
 * renders `otherUser.name` and `@{otherUser.username}` unguarded, because it is a profile header
 * with a call to action attached, not a generic app-handoff. A challenge is not a person, and
 * inventing an `otherUser` to satisfy it would mean exposing a team's identity through the public
 * read this page relies on.
 *
 * What is reused is the thing that actually does the work: `openMobileAppLink`, the same primitive
 * the modal calls, which tries the app and falls back to the right store for the device.
 *
 * ## Why the challenge's media is rendered, and when it is not
 *
 * This page used to show a name and no media at all. That was a deliberate decision and it is
 * deliberately reversed: a fan asked to install an application on the strength of one sentence has
 * nothing to decide with, and the campaign's own Click Rate led here.
 *
 * The visibility question it was avoiding is answered on the server instead, by the read below,
 * using the predicate the platform's public feeds already use — shared with anyone, no
 * subscription, moderation cleared. So this screen never decides who may see what. It renders the
 * `media` block if it was given one and a named stand-in if it was not, and it cannot tell which
 * of the several reasons applied. That is the point: a second copy of a visibility rule is a second
 * rule.
 *
 * ## Why the clip does not autoplay
 *
 * Browsers block unmuted autoplay without a prior gesture, and a blocked autoplay leaves a *paused*
 * video — a black rectangle, which reads as broken where a silent one reads as deliberate. More
 * importantly, every recipient of every campaign reaches this page, so autoloading a clip would be
 * bandwidth spent on fans who are still deciding whether they care. The poster is one small image;
 * the clip is fetched when somebody asks for it.
 */

/** What the app is asked to open. Mirrors the web path, which the app already routes internally. */
const appPathFor = (id) => `fancam/${id}`;

/**
 * A portrait frame, because a FanCam is shot on a phone.
 *
 * `aspectRatio` is not guaranteed at ingest, so an absent one is treated as portrait rather than as
 * unknown — a 16:9 clip in a portrait frame is cropped, while a portrait clip in a landscape frame
 * is letterboxed into a thin strip with black on both sides. Cropping is the better wrong answer.
 */
const Frame = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
  aspect-ratio: ${(props) => props.$ratio};
  border-radius: 14px;
  overflow: hidden;
  background: ${(props) => props.$bg};
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => props.$fit};
  display: block;
`;

const PlayerSlot = styled.div`
  position: absolute;
  inset: 0;

  /* react-player wraps the <video> in a div; both must fill the frame, and the video itself needs
     object-fit or it letterboxes inside its own box. */
  video {
    width: 100% !important;
    height: 100% !important;
    object-fit: ${(props) => props.$fit};
  }
`;

const PlayButton = styled.button`
  position: absolute;
  inset: 0;
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    width: 62px;
    height: 62px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 4px;
  }
`;

const StandIn = styled.div`
  padding: 0 22px;
  text-align: center;
`;

const FanCamChallenge = () => {
  const { id } = useParams();

  // `undefined` while the lookup is in flight, `null` once it has failed. The two must not be
  // conflated: a spinner that never resolves is how a deleted challenge would otherwise present.
  const [challenge, setChallenge] = useState(undefined);
  const [playing, setPlaying] = useState(false);

  /*
    A poster that 404s must not leave a broken-image icon.

    The read can hand out an address that no longer resolves — an expired signature, an object
    removed from the bucket — and a browser draws that as a broken-image glyph with the alt text
    beside it. Falling back to the stand-in keeps the page looking intentional in the one case
    nobody tests by hand.
  */
  const [posterFailed, setPosterFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      setChallenge(undefined);
      setPlaying(false);
      setPosterFailed(false);

      const res = await Api.get(`/team/fancam/challenge/publicGet/${id}`, null, { silent: true });

      if (cancelled) return;

      /*
        `res.data`, not `res`.

        `Api.get` returns the whole response body, and every endpoint on this platform wraps its
        payload in a `data` key — so the challenge is at `res.data`, exactly as every other screen
        reads it (`setCampaigns(res?.data || [])`, and so on).

        Read as `res.caption` this was always `undefined`, so every challenge in existence rendered
        "This challenge has ended" — including the ones a campaign had just sent twelve thousand
        people to. It survived review and a test suite because the test mocked `Api.get` returning
        the *unwrapped* object, which is not a shape this client can produce.
      */
      const challengeData = res?.data;

      // A malformed id, a deleted challenge and an id that is not a challenge all arrive here the
      // same way, and are shown the same thing — a fan cannot act differently on any of them.
      setChallenge(challengeData?.caption ? challengeData : null);
    }

    resolve();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (challenge === undefined) {
    return (
      <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll>
        <Spinner2 col={theme.SECONDARY} />
      </Col>
    );
  }

  if (challenge === null) {
    return (
      <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll pad="0 24px">
        <Text size="24px" marg="0 0 12px">
          This challenge has ended
        </Text>
        <Text size="16px" regular col={theme.GREY_60} marg="0 0 24px" center>
          The FanCam Challenge this link points to is no longer available. There are others running
          in the app.
        </Text>
        <Button
          text="Get the app"
          big
          wid="240px"
          bg={theme.SECONDARY}
          onClick={() => openMobileAppLink()}
        />
      </Col>
    );
  }

  const { media } = challenge;
  const showMedia = Boolean(media?.thumbnail || media?.url) && !posterFailed;
  const ratio = media?.aspectRatio || 0.5625;
  const fit = ratio < 0.9 ? 'cover' : 'contain';
  const isVideo = media?.type === 'video';

  return (
    <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll pad="0 24px">
      <Text size="14px" med col={theme.SECONDARY} marg="0 0 8px">
        FANCAM CHALLENGE
      </Text>

      <Frame $ratio={ratio} $bg={showMedia ? '#000' : theme.GREY_10 || '#EEF1F6'}>
        {showMedia ? (
          <>
            <Poster
              src={media.thumbnail || media.url}
              /*
                Empty alt, on purpose and by the same reasoning as the email marker's.

                The caption sits directly below this frame and names the challenge; describing the
                still as well would make a screen reader announce the same thing twice, the second
                time in words nobody wrote. The image is illustrative of text that is already
                present, which is what decorative means.
              */
              alt=""
              $fit={fit}
              onError={() => setPosterFailed(true)}
            />
            {isVideo && !playing ? (
              <PlayButton type="button" aria-label="Play the challenge clip" onClick={() => setPlaying(true)}>
                <span>▶</span>
              </PlayButton>
            ) : null}
            {isVideo && playing ? (
              <PlayerSlot $fit={fit}>
                <ReactPlayer url={media.url} playing controls width="100%" height="100%" />
              </PlayerSlot>
            ) : null}
          </>
        ) : (
          <StandIn>
            {/*
              "FanCam" rather than "clip", because a challenge may be a photo — `media.type` is
              `image` about as often as it is `video`, and a page telling a fan its *clip* could not
              be loaded when the thing is a photograph reads as written by someone who never saw it.

              The two reasons stay separate. "Not shared publicly" is the owner's decision and is
              the end of it; "could not be loaded" is a fault on our side, and a fan who reloads may
              well see it. Collapsing them into one sentence would mean telling half of them
              something untrue.
            */}
            <Text size="14px" regular col={theme.GREY_60} center>
              {posterFailed
                ? "This challenge's FanCam could not be loaded. You can still take part in the app."
                : "This challenge's FanCam is not shared publicly. You can still take part in the app."}
            </Text>
          </StandIn>
        )}
      </Frame>

      <Text size="28px" marg="0 0 12px" center>
        {challenge.caption}
      </Text>
      <Text size="16px" regular col={theme.GREY_60} marg="0 0 24px" center>
        Entries are posted from the app. Open it to submit your photo or video.
      </Text>
      <Button
        text="Submit photo/video"
        big
        wid="240px"
        bg={theme.SECONDARY}
        onClick={() => openMobileAppLink(appPathFor(id))}
      />
    </Col>
  );
};

export default FanCamChallenge;
